import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Box, Chip, FormHelperText, Paper, Stack, Typography } from "@mui/material";
import { APP_UI } from "../ui/appUi";

export default function AppSelectionField({
  label,
  value,
  placeholder,
  chips,
  onDeleteChip,
  onClick,
  required = false,
  error = false,
  helperText,
}: {
  label: string;
  value?: string;
  placeholder: string;
  chips?: string[];
  onDeleteChip?: (value: string) => void;
  onClick: () => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}) {
  return (
    <Box>
    <Box
      component="fieldset"
      sx={{
        width: "100%",
        minWidth: 0,
        minHeight: 58,
        m: 0,
        p: 0,
        borderRadius: APP_UI.fieldRadius,
        border: `1px solid ${error ? APP_UI.error : APP_UI.borderStrong}`,
        bgcolor: APP_UI.card,
        transition: "border-color 140ms ease, box-shadow 140ms ease",
        "&:hover": { borderColor: "rgba(255,255,255,0.55)" },
        "&:focus-within": {
          borderColor: APP_UI.accent,
          boxShadow: `0 0 0 1px ${APP_UI.accent}`,
        },
      }}
    >
      <Box
        component="legend"
        sx={{
          ml: 1.15,
          px: 0.4,
          color: error ? APP_UI.error : "rgba(255,255,255,.72)",
          fontSize: "0.75rem",
          lineHeight: 1,
          fontWeight: 400,
          whiteSpace: "nowrap",
        }}
      >
        {label}{required ? " *" : ""}
      </Box>

      <Paper
        component="button"
        type="button"
        onClick={onClick}
        aria-label={label}
        elevation={0}
        sx={{
          width: "100%",
          minHeight: 48,
          mt: -0.15,
          px: 1.5,
          pb: 1.05,
          pt: 0.75,
          border: 0,
          borderRadius: APP_UI.fieldRadius,
          bgcolor: "transparent",
          color: "inherit",
          textAlign: "left",
          font: "inherit",
          display: "flex",
          alignItems: "center",
          gap: 1,
          outline: "none",
          cursor: "pointer",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {chips && chips.length > 0 ? (
            <Stack direction="row" useFlexGap spacing={0.65} sx={{ flexWrap: "wrap" }}>
              {chips.map((chip) => (
                <Chip
                  key={chip}
                  label={chip}
                  onDelete={onDeleteChip ? (event) => {
                    event.stopPropagation();
                    onDeleteChip(chip);
                  } : undefined}
                  sx={{
                    color: "#bfeef7",
                    bgcolor: APP_UI.accentSoft,
                    border: `1px solid ${APP_UI.accentBorder}`,
                    fontWeight: 750,
                    "& .MuiChip-deleteIcon": { color: APP_UI.accent },
                  }}
                />
              ))}
            </Stack>
          ) : (
            <Typography
              variant="body1"
              sx={{
                color: value ? APP_UI.text : "rgba(255,255,255,.42)",
                fontWeight: value ? 700 : 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value || placeholder}
            </Typography>
          )}
        </Box>
        <ChevronRightRoundedIcon sx={{ color: APP_UI.accent, flex: "0 0 auto" }} />
      </Paper>
    </Box>
    {helperText && <FormHelperText error={error} sx={{ mx: 1.75, mt: 0.35 }}>{helperText}</FormHelperText>}
    </Box>
  );
}
